import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { listPlanes, getDashboard } from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import {
  Plus, FileText, ChevronRight, ShieldCheck, Building2, ClipboardCheck,
  AlertTriangle, ListChecks, CalendarClock, CircleCheck, Clock, CircleAlert,
} from "lucide-react";

const ESTADO_BADGE = {
  borrador: "bg-slate-100 text-slate-600 border-slate-200",
  en_revision: "bg-amber-50 text-amber-700 border-amber-200",
  generado: "bg-blue-50 text-blue-700 border-blue-200",
};
const ESTADO_LABEL = { borrador: "Borrador", en_revision: "En revisión", generado: "Generado" };

const URGENCIA = {
  vencida: { label: "Vencida", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500", Icon: CircleAlert },
  proxima: { label: "Próxima", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", Icon: Clock },
  en_plazo: { label: "En plazo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", Icon: CircleCheck },
};

function MetricCard({ icon: Icon, label, value, accent, testid, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 transition-colors disabled:cursor-default"
      disabled={!onClick}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-3xl font-heading font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </button>
  );
}

function diasTexto(d) {
  if (d < 0) return `Venció hace ${Math.abs(d)} día${Math.abs(d) === 1 ? "" : "s"}`;
  if (d === 0) return "Vence hoy";
  return `Vence en ${d} día${d === 1 ? "" : "s"}`;
}

export default function Inicio() {
  const [planes, setPlanes] = useState([]);
  const [dash, setDash] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    listPlanes().then(setPlanes).catch(() => {});
    getDashboard().then(setDash).catch(() => {});
  }, []);

  const t = dash?.totales || {};
  const vencimientos = dash?.vencimientos || [];

  return (
    <Layout title="Inicio" subtitle="Panel de control · Genera y da seguimiento a tus Planes de PRL">
      {/* CTA */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-700 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-slate-900">Nuevo Plan de PRL</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-lg">
              Empresa → Centro → Puestos y trabajadores → Visita técnica → Análisis IA → Revisión → Plan completo en Word y PDF.
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate("/planes/nuevo")}
          data-testid="inicio-nuevo-plan"
          className="inline-flex items-center gap-2 h-14 px-7 rounded-xl bg-blue-700 text-white font-semibold text-base hover:bg-blue-800 transition-colors shrink-0"
        >
          <Plus className="w-5 h-5" /> Nuevo Plan de PRL
        </button>
      </div>

      {/* Métricas */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-metricas">
        <MetricCard icon={FileText} label="Planes de PRL" value={t.planes ?? "—"} accent="bg-blue-50 text-blue-700" testid="metric-planes" onClick={() => navigate("/planes")} />
        <MetricCard icon={Building2} label="Empresas" value={t.empresas ?? "—"} accent="bg-indigo-50 text-indigo-700" testid="metric-empresas" onClick={() => navigate("/empresas")} />
        <MetricCard icon={ClipboardCheck} label="Planes generados" value={t.generado ?? "—"} accent="bg-emerald-50 text-emerald-700" testid="metric-generados" onClick={() => navigate("/planes?estado=generado")} />
        <MetricCard icon={ListChecks} label="En revisión" value={t.en_revision ?? "—"} accent="bg-amber-50 text-amber-700" testid="metric-revision" onClick={() => navigate("/planes?estado=en_revision")} />
        <MetricCard icon={AlertTriangle} label="Deficiencias abiertas" value={t.deficiencias_abiertas ?? "—"} accent="bg-orange-50 text-orange-700" testid="metric-deficiencias" />
        <MetricCard icon={ListChecks} label="Medidas pendientes" value={t.medidas_pendientes ?? "—"} accent="bg-purple-50 text-purple-700" testid="metric-medidas" />
        <MetricCard icon={CircleAlert} label="Vencimientos vencidos" value={t.vencidos ?? "—"} accent="bg-red-50 text-red-700" testid="metric-vencidos" />
        <MetricCard icon={Clock} label="Próximos (30 días)" value={t.proximos ?? "—"} accent="bg-amber-50 text-amber-700" testid="metric-proximos" />
      </div>

      <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Panel de vencimientos */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-5 h-5 text-blue-700" />
            <h3 className="font-heading font-semibold text-lg text-slate-900">Vencimientos y avisos</h3>
          </div>
          {vencimientos.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="vencimientos-vacio">
              No hay vencimientos programados. Genera un Plan o registra una visita para ver aquí los recordatorios de revisión anual.
            </div>
          ) : (
            <div className="space-y-3" data-testid="vencimientos-lista">
              {vencimientos.slice(0, 8).map((v, i) => {
                const u = URGENCIA[v.urgencia] || URGENCIA.en_plazo;
                return (
                  <button
                    key={`${v.link}-${i}`}
                    data-testid={`vencimiento-${i}`}
                    onClick={() => navigate(v.link)}
                    className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors flex items-center gap-3"
                  >
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${u.dot}`} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{v.titulo}</div>
                      <div className="text-xs text-slate-500 truncate">{v.empresa} · {v.detalle}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{diasTexto(v.dias_restantes)} · {formatFecha(v.fecha)}</div>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border inline-flex items-center gap-1 shrink-0 ${u.cls}`}>
                      <u.Icon className="w-3.5 h-3.5" /> {u.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Planes recientes */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg text-slate-900">Planes recientes</h3>
            {planes.length > 0 && (
              <button onClick={() => navigate("/planes")} className="text-sm font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">
                Ver todos <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          {planes.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">
              Aún no hay planes. Pulsa <span className="font-semibold text-slate-700">Nuevo Plan de PRL</span> para empezar.
            </div>
          ) : (
            <div className="space-y-3">
              {planes.slice(0, 6).map((p) => (
                <button
                  key={p.id}
                  data-testid={`inicio-plan-${p.id}`}
                  onClick={() => navigate(`/planes/${p.id}`)}
                  className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-semibold text-slate-900 truncate">{p.titulo}</div>
                    <div className="text-xs text-slate-500">{p.empresa_nombre} · {formatFecha(p.updated_at)}</div>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border shrink-0 ${ESTADO_BADGE[p.estado] || ESTADO_BADGE.borrador}`}>
                    {ESTADO_LABEL[p.estado] || p.estado}
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
